import React, { useState, useEffect } from "react";
import { changeEmail } from "shared/api/client/orvalHelpers";
import { parseApiError } from "shared/api/client/errorParser";
import "styles/legacy/change-password-modal.css";

interface ChangeEmailModalProps {
  open: boolean;
  onClose: () => void;
  setSuccessMessage: (msg: string) => void;
  onEmailChanged?: (email: string) => void;
}

const ChangeEmailModal: React.FC<ChangeEmailModalProps> = ({
  open,
  onClose,
  setSuccessMessage,
  onEmailChanged,
}) => {
  const [newEmail, setNewEmail] = useState("");
  const [repeatEmail, setRepeatEmail] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (!open) {
      setNewEmail("");
      setRepeatEmail("");
      setCurrentPassword("");
      setErrors({});
    }
  }, [open]);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const newErrors: { [key: string]: string } = {};
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!emailPattern.test(newEmail)) {
      newErrors.Email = "Please enter a valid email address";
    }
    if (newEmail !== repeatEmail) {
      newErrors.RepeatEmail = "Email addresses dont match";
    }
    if (!currentPassword) {
      newErrors.Password = "Password is required";
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      try {
        await changeEmail(currentPassword, newEmail);
        onEmailChanged?.(newEmail.trim());

        setSuccessMessage("Verification email sent to your new address!");
        setTimeout(() => setSuccessMessage(""), 3000);
        onClose();
      } catch (error: any) {
        const message = parseApiError(error, "Failed to change email.");
        let newErrors: { [key: string]: string } = {};

        if (
          message.includes("Current password is incorrect")
        ) {
          newErrors.Password = "Password is incorrect";
        } else if (
          message.includes("Email already exists")
        ) {
          newErrors.Email = "Email already exists";
        } else {
          newErrors.api = message;
        }

        setErrors(newErrors);
        console.error(error);
      }
    }
  };

  return (
    <div className="change-password-modal-overlay" onClick={onClose}>
      <div
        className="change-password-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="change-password-top-div">
          <div className="change-password-modal-title">Change Email</div>
          <button className="change-password-modal-close-btn" onClick={onClose}>
            <img
              src="/images/icons/close-button-change-password-modal.svg"
              alt="close button"
              style={{ cursor: "pointer" }}
            />
          </button>
        </div>
        <div className="modal-divider" />
        <div className="change-password-mid-div">
          <form
            className="change-password-form"
            onSubmit={handleSubmit}
            autoComplete="off"
          >
            {errors.api && <div className="change-password-error" role="alert">{errors.api}</div>}
            <div className="change-password-modal-form-group">
              <label className="change-password-modal-label">Email</label>
              <input
                className="change-password-modal-input"
                type="text"
                autoComplete="off"
                placeholder="Enter new email address"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
              />
              {errors.Email && (
                <div className="change-password-error">{errors.Email}</div>
              )}
            </div>
            <div className="change-password-modal-form-group">
              <label className="change-password-modal-label">
                Repeat Email
              </label>
              <input
                className="change-password-modal-input"
                type="text"
                placeholder="Repeat new email address"
                value={repeatEmail}
                onChange={(e) => setRepeatEmail(e.target.value)}
              />
              {errors.RepeatEmail && (
                <div className="change-password-error">
                  {errors.RepeatEmail}
                </div>
              )}
            </div>

            <div className="change-password-modal-form-group">
              <label className="change-password-modal-label">Password</label>
              <input
                className="change-password-modal-input"
                type="password"
                autoComplete="new-password"
                placeholder="Enter password to confirm"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
              />
              {errors.Password && (
                <div className="change-password-error">{errors.Password}</div>
              )}
            </div>
            <div className="modal-divider" />
            <div className="modal-actions">
              <button type="button" className="cancel-button" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="reset-button">
                Change email
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangeEmailModal;
